// PATH: src/components/documents/documentChapter.ts
/**
 * Identité de chapitre d'un document : logique pure (testée), sans React.
 *
 * Les quatre champs `chapter_ref`, `chapter_order`, `semester`, `track_id` ne
 * s'écrivent QUE par `PATCH /v1/documents/:id/chapter` : le `PUT` du document
 * réécrit toutes ses colonnes, il ne doit donc jamais les porter.
 */
import {
  CHAPTER_FIELDS,
  type Document,
  type DocumentChapterPatch,
} from "../../models/domain";

/** Ce que le `PUT /v1/documents/:id` reçoit : le document entier, hors chapitre. */
export type DocumentPutPayload = Omit<
  Document,
  "id" | "created_at" | "updated_at" | (typeof CHAPTER_FIELDS)[number]
>;

/** Ce que l'onglet « Informations » modifie ; le reste du document est repris tel quel. */
export type DocumentMetadataEdits = Partial<
  Pick<
    Document,
    | "title"
    | "description"
    | "language"
    | "tags"
    | "is_active"
    | "is_exam"
    | "is_exercice"
    | "is_course"
  >
>;

/**
 * Payload du PUT : le document courant (le serveur réécrit toutes les colonnes,
 * un champ absent serait remis à zéro) avec les modifications du formulaire,
 * et SANS les champs de chapitre.
 */
export const buildDocumentPutPayload = (
  doc: Document,
  edits: DocumentMetadataEdits,
): DocumentPutPayload => {
  const base: Record<string, unknown> = { ...doc, ...edits };
  delete base.id;
  delete base.created_at;
  delete base.updated_at;
  CHAPTER_FIELDS.forEach((field) => {
    delete base[field];
  });
  return base as DocumentPutPayload;
};

/** Formulaire du bloc « Chapitre » : chaînes brutes, "" = vide. */
export interface ChapterForm {
  chapterRef: string;
  chapterOrder: string;
  semester: string;
  /** "" = commun à toutes les filières. */
  trackId: string;
}

export const chapterFormFromDocument = (doc: Partial<Document> | null | undefined): ChapterForm => ({
  chapterRef: doc?.chapter_ref ?? "",
  chapterOrder: doc?.chapter_order != null ? String(doc.chapter_order) : "",
  semester: doc?.semester != null ? String(doc.semester) : "",
  trackId: doc?.track_id ?? "",
});

/** Entier ≥ 1, ou null si vide. `undefined` = saisie invalide. */
export const parsePositiveIntOrNull = (raw: string): number | null | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  const n = Number(trimmed);
  return n >= 1 ? n : undefined;
};

export type ChapterPatchResult =
  | { ok: true; patch: DocumentChapterPatch }
  | { ok: false; error: string };

/**
 * Construit le corps du PATCH : seules les clés modifiées sont envoyées
 * (contrat : « seules les clés présentes sont écrites, null efface »).
 */
export const buildChapterPatch = (
  doc: Partial<Document> | null | undefined,
  form: ChapterForm,
): ChapterPatchResult => {
  const chapterOrder = parsePositiveIntOrNull(form.chapterOrder);
  if (chapterOrder === undefined) {
    return { ok: false, error: "L'ordre du chapitre doit être un entier ≥ 1." };
  }
  const semester = parsePositiveIntOrNull(form.semester);
  if (semester === undefined) {
    return { ok: false, error: "Le semestre doit être un entier ≥ 1." };
  }

  const next: Required<DocumentChapterPatch> = {
    chapter_ref: form.chapterRef.trim() || null,
    chapter_order: chapterOrder,
    semester,
    track_id: form.trackId || null,
  };

  const patch: DocumentChapterPatch = {};
  CHAPTER_FIELDS.forEach((field) => {
    const current = doc?.[field] ?? null;
    if (current !== next[field]) {
      (patch as Record<string, unknown>)[field] = next[field];
    }
  });
  return { ok: true, patch };
};

/** Libellé court « S1 · 3 » (semestre · ordre), "" si aucun des deux. */
export const formatChapterPosition = (doc: Partial<Document>): string => {
  const parts: string[] = [];
  if (doc.semester != null) parts.push(`S${doc.semester}`);
  if (doc.chapter_order != null) parts.push(String(doc.chapter_order));
  return parts.join(" · ");
};
